import CheckBoxIcon from "@mui/icons-material/CheckBox";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import NotificationImportantIcon from "@mui/icons-material/NotificationImportant";
import NotificationsIcon from "@mui/icons-material/Notifications";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import SnoozeIcon from "@mui/icons-material/Snooze";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import {
	Button,
	IconButton,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	Tooltip,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { isPast } from "date-fns";
import { useEffect, useState } from "react";
import {
	createReminderForObject,
	getRemindersForObject,
	Reminder,
	ReminderStates,
	updateReminder,
} from "../../api/reminders/reminders";
import {
	formatDate,
	formatRelativeTime,
	formatShortTime,
} from "../../utils/date-formatting";
import { DatePickerDialog, getDefaultFutureDate } from "../common/DatePickerDialog";
import { useGlobalState } from "../globalState";

type Props = {
	objectName: string;
	objectId: number;
};

export const ReminderView = ({ objectName, objectId }: Props) => {
	const theme = useTheme();
	const { currentUser } = useGlobalState();
	const [loading, setLoading] = useState<boolean>(true);
	const [reminders, setReminders] = useState<Reminder[]>([]);
	const [showHistory, setShowHistory] = useState<boolean>(false);

	// Add dialog state
	const [addDialogOpen, setAddDialogOpen] = useState(false);
	const [currentNote, setCurrentNote] = useState<string>("");

	// Snooze dialog state
	const [snoozeDialogOpen, setSnoozeDialogOpen] = useState(false);
	const [snoozingReminder, setSnoozingReminder] = useState<Reminder | null>(null);

	// Edit dialog state
	const [editDialogOpen, setEditDialogOpen] = useState(false);
	const [editingReminder, setEditingReminder] = useState<Reminder | null>(null);

	useEffect(() => {
		getRemindersForObject(objectName, objectId)
			.then(setReminders)
			.then(() => setLoading(false));
	}, [objectName, objectId]);

	const handleOpenAddDialog = () => {
		setCurrentNote("");
		setAddDialogOpen(true);
	};

	const handleCloseAddDialog = () => {
		setAddDialogOpen(false);
	};

	const handleSaveReminder = (selectedDate: Date) => {
		const newReminder: Reminder = {
			note: currentNote,
			due_date: selectedDate.toISOString(),
			user_id: currentUser.id,
			content_name: "",
			content_type: -1,
			user_name: "",
			created: "",
			updated: "",
			state: ReminderStates.ACTIVE,
			object_id: -1,
			id: -1,
		};
		createReminderForObject(objectName, objectId, newReminder).then((res) => {
			setReminders([res, ...reminders]);
			setAddDialogOpen(false);
		});
	};

	const handleUpdate = (reminder: Reminder, state: number, dueDateIso?: string) => {
		const updatedReminder = {
			...reminder,
			state,
			due_date: dueDateIso ?? reminder.due_date,
		};
		updateReminder(updatedReminder).then((res) => {
			setReminders(reminders.map((r) => (r.id === reminder.id ? res : r)));
		});
	};

	const handleOpenSnoozeDialog = (reminder: Reminder) => {
		setSnoozingReminder(reminder);
		setSnoozeDialogOpen(true);
	};

	const handleCloseSnoozeDialog = () => {
		setSnoozeDialogOpen(false);
		setSnoozingReminder(null);
	};

	const handleSaveSnooze = (snoozeDate: Date) => {
		if (!snoozingReminder) return;
		handleUpdate(
			snoozingReminder,
			ReminderStates.ACTIVE_SNOOZED,
			snoozeDate.toISOString()
		);
		setSnoozeDialogOpen(false);
		setSnoozingReminder(null);
	};

	const handleOpenEditDialog = (reminder: Reminder) => {
		setEditingReminder(reminder);
		setCurrentNote(reminder.note ?? "");
		setEditDialogOpen(true);
	};

	const handleCloseEditDialog = () => {
		setEditDialogOpen(false);
		setEditingReminder(null);
	};

	const handleSaveEdit = (selectedDate: Date) => {
		if (!editingReminder) return;
		const updatedReminder = {
			...editingReminder,
			due_date: selectedDate.toISOString(),
			note: currentNote,
		};
		updateReminder(updatedReminder).then((res) => {
			setReminders(reminders.map((r) => (r.id === editingReminder.id ? res : r)));
			handleCloseEditDialog();
		});
	};

	const hasHistory = reminders.some((r) => r.state > 1);

	const activeReminders = reminders
		.filter((r) => r.state <= 1)
		.sort(
			(a, b) => new Date(a.due_date).getTime() - new Date(b.due_date).getTime()
		);

	const historyReminders = reminders
		.filter((r) => r.state > 1)
		.sort(
			(a, b) => new Date(b.due_date).getTime() - new Date(a.due_date).getTime()
		);

	const displayReminders = showHistory
		? [...activeReminders, ...historyReminders]
		: activeReminders;

	const getStatusIcon = (reminder: Reminder) => {
		const reminderDate = new Date(reminder.due_date);
		const isExpired = isPast(reminderDate);

		if (reminder.state === 2) {
			return (
				<Tooltip title="Done">
					<CheckBoxIcon
						fontSize="small"
						sx={{ color: theme.palette.success.main }}
					/>
				</Tooltip>
			);
		} else if (reminder.state === 3) {
			return (
				<Tooltip title="Deleted">
					<DeleteIcon
						fontSize="small"
						sx={{ color: theme.palette.error.main }}
					/>
				</Tooltip>
			);
		} else if (isExpired) {
			return (
				<Tooltip title="Overdue">
					<NotificationImportantIcon
						fontSize="small"
						sx={{ color: theme.palette.error.main }}
					/>
				</Tooltip>
			);
		} else if (reminder.state === 1) {
			return (
				<Tooltip title="Snoozed">
					<SnoozeIcon
						fontSize="small"
						sx={{ color: theme.palette.warning.main }}
					/>
				</Tooltip>
			);
		}
		return (
			<Tooltip title="Active">
				<NotificationsIcon
					fontSize="small"
					sx={{ color: theme.palette.success.main }}
				/>
			</Tooltip>
		);
	};

	const getDueDisplay = (reminder: Reminder) => {
		const reminderDate = new Date(reminder.due_date);
		const isExpired = isPast(reminderDate);
		const relTime = formatRelativeTime(reminderDate);
		const dateStr = formatDate(reminder.due_date);
		const hasTime =
			reminderDate.getHours() !== 0 || reminderDate.getMinutes() !== 0;
		const dateTimeStr = hasTime
			? `${dateStr} ${formatShortTime(reminderDate)}`
			: dateStr;
		const timeStr = isExpired ? `${relTime} ago` : `in ${relTime}`;
		return `${dateTimeStr} (${timeStr})`;
	};

	if (loading) {
		return null;
	}

	return (
		<>
			<TableContainer data-component="reminders-table">
				<Table size="small">
					<TableHead>
						<TableRow>
							<TableCell>Status</TableCell>
							<TableCell>User</TableCell>
							<TableCell>Due</TableCell>
							<TableCell>Note</TableCell>
							<TableCell align="right">Actions</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{displayReminders.length === 0 ? (
							<TableRow data-component="no-reminders-row">
								<TableCell colSpan={5} align="center">
									No reminders
								</TableCell>
							</TableRow>
						) : (
							displayReminders.map((reminder) => {
								const isHistory = reminder.state > 1;
								const isActive =
									reminder.state === 0 || reminder.state === 1;
								return (
									<TableRow
										key={reminder.id}
										data-component="reminder-row"
										sx={{ opacity: isHistory ? 0.6 : 1 }}
									>
										<TableCell>{getStatusIcon(reminder)}</TableCell>
										<TableCell>
											{reminder.user_first_name}
										</TableCell>
										<TableCell>{getDueDisplay(reminder)}</TableCell>
										<TableCell
											sx={{
												maxWidth: 200,
												overflow: "hidden",
												textOverflow: "ellipsis",
												whiteSpace: "nowrap",
											}}
										>
											<Tooltip title={reminder.note || ""}>
												<span>{reminder.note}</span>
											</Tooltip>
										</TableCell>
										<TableCell align="right">
											{isActive && (
												<Stack
													direction="row"
													spacing={0.5}
													justifyContent="flex-end"
												>
													<Tooltip title="Edit">
														<IconButton
															data-component="edit-reminder-button"
															size="small"
															onClick={() =>
																handleOpenEditDialog(
																	reminder
																)
															}
														>
															<EditIcon fontSize="small" />
														</IconButton>
													</Tooltip>
													<Tooltip title="Mark done">
														<IconButton
															data-component="done-reminder-button"
															size="small"
															onClick={() =>
																handleUpdate(
																	reminder,
																	2
																)
															}
														>
															<CheckBoxIcon fontSize="small" />
														</IconButton>
													</Tooltip>
													<Tooltip title="Snooze">
														<IconButton
															data-component="snooze-reminder-button"
															size="small"
															onClick={() =>
																handleOpenSnoozeDialog(
																	reminder
																)
															}
														>
															<SnoozeIcon fontSize="small" />
														</IconButton>
													</Tooltip>
													<Tooltip title="Delete">
														<IconButton
															data-component="delete-reminder-button"
															size="small"
															onClick={() =>
																handleUpdate(
																	reminder,
																	3
																)
															}
														>
															<DeleteIcon fontSize="small" />
														</IconButton>
													</Tooltip>
												</Stack>
											)}
										</TableCell>
									</TableRow>
								);
							})
						)}
					</TableBody>
				</Table>
			</TableContainer>

			<Stack
				direction="row"
				justifyContent="flex-end"
				alignItems="center"
				spacing={1}
				sx={{ mt: 2 }}
				data-component="reminder-footer"
			>
				{hasHistory && (
					<Button
						variant="outlined"
						size="small"
						startIcon={
							showHistory ? <VisibilityOffIcon /> : <VisibilityIcon />
						}
						onClick={() => setShowHistory(!showHistory)}
						sx={{ mr: "auto" }}
						data-component="toggle-history-button"
					>
						{showHistory ? "Hide history" : "Show history"}
					</Button>
				)}
				<Button
					variant="outlined"
					size="small"
					startIcon={<NotificationsActiveIcon />}
					onClick={handleOpenAddDialog}
					data-component="add-reminder-button"
				>
					Add Reminder
				</Button>
			</Stack>

			<DatePickerDialog
				open={addDialogOpen}
				onClose={handleCloseAddDialog}
				onSave={handleSaveReminder}
				title="Add Reminder"
				initialDate={getDefaultFutureDate(1)}
				dataComponent="add-reminder-dialog"
				withTime
				extraContent={
					<TextField
						label="Note (optional)"
						fullWidth
						multiline
						rows={2}
						value={currentNote}
						onChange={(ev) => setCurrentNote(ev.target.value)}
						data-component="reminder-note-input"
					/>
				}
			/>

			<DatePickerDialog
				open={snoozeDialogOpen}
				onClose={handleCloseSnoozeDialog}
				onSave={handleSaveSnooze}
				title="Snooze Reminder"
				saveButtonText="Snooze"
				initialDate={getDefaultFutureDate(1)}
				dataComponent="snooze-reminder-dialog"
				withTime
			/>

			{editingReminder && (
				<DatePickerDialog
					open={editDialogOpen}
					onClose={handleCloseEditDialog}
					onSave={handleSaveEdit}
					title="Edit Reminder"
					initialDate={new Date(editingReminder.due_date)}
					initialTime={new Date(editingReminder.due_date)}
					dataComponent="edit-reminder-dialog"
					withTime
					extraContent={
						<TextField
							label="Note (optional)"
							fullWidth
							multiline
							rows={2}
							value={currentNote}
							onChange={(ev) => setCurrentNote(ev.target.value)}
							data-component="edit-reminder-note-input"
						/>
					}
				/>
			)}
		</>
	);
};
